export interface AnchorRect { x: number; y: number; w: number; h: number }
export interface ContentSize { w: number; h: number }
export interface Viewport { w: number; h: number }

export interface PlacementInput {
  anchor: AnchorRect;
  contentSize: ContentSize;
  viewport: Viewport;
  /** Margin between tooltip and the viewport edges. */
  vpad: number;
  /** Margin between tooltip edge and the anchor edge. */
  anchorGap: number;
}

export interface Placement {
  x: number;
  y: number;
  placedAbove: boolean;
}

/**
 * Pure placement math for the tooltip. Caller passes anchor + content size
 * (already measured in scene px) and gets back the top-left corner to draw.
 *
 * Vertical: prefer above; flip below when above won't fit; if neither
 * fits cleanly, clamp to the bottom edge of the viewport.
 *
 * Horizontal: center on the anchor; clamp to viewport.
 */
export function computeTooltipPlacement(input: PlacementInput): Placement {
  const { anchor, contentSize, viewport, vpad, anchorGap } = input;
  const aboveY = anchor.y - anchorGap - contentSize.h;
  const belowY = anchor.y + anchor.h + anchorGap;
  const placedAbove = aboveY >= vpad;

  let y: number;
  if (placedAbove) {
    y = aboveY;
  } else if (belowY + contentSize.h <= viewport.h - vpad) {
    y = belowY;
  } else {
    // Doesn't fit cleanly above or below — clamp to the viewport bottom.
    y = viewport.h - contentSize.h - vpad;
  }

  const centerX = anchor.x + anchor.w / 2;
  let x = Math.round(centerX - contentSize.w / 2);
  x = Math.max(vpad, Math.min(x, viewport.w - contentSize.w - vpad));

  return { x, y, placedAbove };
}
